// BoardJob — the PUBLIC job detail page (/jobs/:id).
//
// This is where a shared link lands and where a board row leads. Anonymous
// visitors read the full JD here — the list stays button-free by design, and
// this page's bottom card is the product's single conversion moment: pitched
// only after the visitor has read the whole posting, when "tailor a CV for
// exactly this job" has an obvious referent.
//
// Query discipline (the PublicShell rule): this page mounts exactly two
// queries — the board-job fetch and the shared session query the hooks use.
// Anything personal (profile, cv-status, jobs) would 401 for anonymous
// visitors and hard-bounce them off the page.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import PageHeader from '../components/PageHeader';
import LogoSquare from '../components/ui/LogoSquare';
import Chip from '../components/ui/Chip';
import { useZh, useProductReady, useSignedIn, useOpenSignup } from '../lib/useProduct';
import {
  ROLE_LABELS, INDUSTRY_LABELS, salaryText, daysAgo, displayCompany,
  AddToPipeline, useBoardJob, prepareJdMarkdown, JobTitle, useContentLang,
  getZhTranslator, jdZhCache,
} from '../components/domain/board';
import type { RoleLevel } from '../components/domain/board';

const SOURCE_LABELS: Record<string, string> = {
  indeed: 'Indeed', linkedin: 'LinkedIn', glassdoor: 'Glassdoor',
};

/**
 * Translate a markdown JD line-by-line, keeping the structural prefixes
 * (heading marks, bullets) out of the translator's hands. Bold markers are
 * stripped from translated lines — the translator scatters asterisks — which
 * costs some emphasis and keeps every list and heading intact.
 */
async function translateMarkdown(translator: { translate(s: string): Promise<string> }, md: string): Promise<string> {
  const out: string[] = [];
  for (const line of md.split('\n')) {
    const m = line.match(/^(\s*(?:#{1,6}\s+|[*-]\s+|\d+\.\s+)?)([\s\S]*)$/);
    const prefix = m?.[1] ?? '';
    const text = (m?.[2] ?? '').trim();
    if (!text) { out.push(line); continue; }
    out.push(prefix + await translator.translate(text.replace(/\*\*/g, '')));
  }
  return out.join('\n');
}

/**
 * The JD body card. Chinese by default (the shared 语言 preference), original
 * one chip away.
 *
 * The translation is the READER's own: Chrome/Edge ship an on-device
 * Translator API (built into the browser, runs locally, costs this product
 * nothing). When the preference is Chinese and the language pack is already
 * on disk, the card translates itself on mount — the original renders
 * immediately and swaps when the translation lands. When the pack still
 * needs downloading (Chrome requires a user gesture for that), or the
 * preference is 原文, a 翻译成中文 button offers the switch instead.
 * Unsupported browsers see the unchanged English card, and the lang="en"
 * attribute below keeps the browser's own full-page translate offer working
 * as the fallback. Deliberately NOT routed through our own LLM pool:
 * inference capacity is reserved for evaluations and CV work.
 *
 * `lang` is the PAGE's useContentLang instance, passed down so the chips
 * here move the title above (and the board next visit) in the same click.
 */
function JdCard({ body, lang }: { body: string; lang: ReturnType<typeof useContentLang> }) {
  const md = useMemo(() => prepareJdMarkdown(body), [body]);
  const [zhText, setZhText] = useState<string | null>(() => jdZhCache.get(md) ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const translate = useCallback(async () => {
    const cached = jdZhCache.get(md);
    if (cached) { setZhText(cached); return; }
    setBusy(true);
    setError('');
    try {
      // The shared singleton (also used for titles) — creating it may first
      // download the language pack; when this runs from the button click,
      // that click is the user gesture Chrome requires.
      const translator = await getZhTranslator();
      const t = await translateMarkdown(translator, md);
      jdZhCache.set(md, t);
      setZhText(t);
    } catch {
      setError('翻译没能完成——可以试试浏览器菜单里的「翻译网页」。');
    } finally {
      setBusy(false);
    }
  }, [md]);

  // 默认中文: translate on mount when the preference says so and no gesture
  // is needed. The zhText/busy/error guards make this one attempt, not a
  // retry loop — a failed auto-translate waits for the user's click.
  useEffect(() => {
    if (lang.active && !zhText && !busy && !error) void translate();
  }, [lang.active, zhText, busy, error, translate]);

  const mode: 'en' | 'zh' = lang.zh && zhText ? 'zh' : 'en';

  return (
    <div className="card" style={{ padding: '26px 28px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
        <div className="tab">岗位描述</div>
        {lang.supported && busy && (
          <span className="page-sub" style={{ marginTop: 0, fontSize: 12.5 }}>正在翻译成中文…</span>
        )}
        {lang.supported && !busy && zhText && (
          <div style={{ display: 'flex', gap: 6 }}>
            <Chip variant={mode === 'en' ? 'primary' : 'default'} onClick={() => lang.setZh(false)}>原文</Chip>
            <Chip variant={mode === 'zh' ? 'primary' : 'default'} onClick={() => lang.setZh(true)}>中文</Chip>
          </div>
        )}
        {lang.supported && !busy && !zhText && (
          <button
            className="btn ghost"
            style={{ fontSize: 12 }}
            onClick={() => { lang.setZh(true); void translate(); }}
          >
            翻译成中文
          </button>
        )}
      </div>
      {error && <div className="page-sub" style={{ marginBottom: 10 }}>{error}</div>}
      {/* jobspy delivers descriptions as markdown (its default
          description_format), so escaped punctuation like \& and \- in the
          raw text is markdown escaping, not noise. lang tells the browser
          what the text actually is — the page declares zh-CN, and without
          this the browser's own translate offer ignores the English JD. */}
      <div className="jd-md" lang={mode === 'zh' ? 'zh' : 'en'}>
        <ReactMarkdown remarkPlugins={[remarkGfm]}>
          {mode === 'zh' && zhText ? zhText : md}
        </ReactMarkdown>
      </div>
    </div>
  );
}

export default function BoardJob() {
  const zh = useZh();
  const productReady = useProductReady();
  const signedIn = useSignedIn();
  const openSignup = useOpenSignup();
  const nav = useNavigate();
  const { id } = useParams();
  const lang = useContentLang();
  const q = useBoardJob(id);

  // Same guard as Board: decide nothing until the product is known, then this
  // surface only exists in uk-cn.
  if (!productReady) return null;
  if (!zh) return <Navigate to="/matches" replace />;

  if (q.isLoading) return <div className="empty">加载中…</div>;

  // One not-found state for every failure shape: bad id (400), pruned or
  // never-existed row (404), transient server error. A pruned row is the
  // common real case — the board ages out postings after 60 days, so old
  // share links are expected to land here.
  if (q.isError || !q.data?.job) {
    return (
      <div className="page-enter">
        <PageHeader
          eyebrow="找不到职位"
          title="这个职位已经不在板子上了。"
          sub="它可能已经关闭或过期下架。职位板每天更新，去看看新的。"
        />
        <button className="btn" onClick={() => nav('/board')}>← 回到职位板</button>
      </div>
    );
  }

  const { job, body } = q.data;
  const pay = salaryText(job);
  const posted = daysAgo(job.posted_at || job.first_seen);
  const source = job.source ? (SOURCE_LABELS[job.source] ?? job.source) : null;

  return (
    <div className="page-enter">
      {/* Back into the browsing context when there is one (the board's filter
          state lives in its URL, so history restores the exact view); a
          shared link opened cold falls back to the board itself. */}
      <button
        className="btn ghost"
        style={{ marginBottom: 20 }}
        onClick={() => (window.history.state?.idx > 0 ? nav(-1) : nav('/board'))}
      >
        ← 返回职位板
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: 24 }}>
        <div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 14 }}>
            <LogoSquare name={job.company || job.title} size={56} />
            <div style={{ flex: 1, minWidth: 0 }}>
              {job.company && <div className="eyebrow">{displayCompany(job.company)}</div>}
              {/* Follows the shared 语言 preference — a translated list
                  leading to an English detail title would read as a bug. */}
              <JobTitle
                title={job.title}
                active={lang.active}
                className="serif"
                style={{ fontSize: 32, lineHeight: 1.12 }}
              />
              <div style={{ fontSize: 13, color: 'var(--ink2)', marginTop: 6 }}>
                {[
                  job.city,
                  pay,
                  posted ? `${posted}发布` : null,
                  source ? `来自 ${source}` : null,
                ].filter(Boolean).join(' · ')}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {job.role_level && (
              <Chip variant="primary">{ROLE_LABELS[job.role_level as RoleLevel] ?? job.role_level}</Chip>
            )}
            {job.industry && <Chip>{INDUSTRY_LABELS[job.industry] ?? job.industry}</Chip>}
          </div>

          {body ? (
            <JdCard body={body} lang={lang} />
          ) : (
            <div className="card" style={{ padding: 22, color: 'var(--ink2)', fontSize: 13.5 }}>
              这个职位还没有完整描述——点右侧「查看原职位」在雇主网站阅读。
            </div>
          )}

          {/* The conversion card. One per page, after the JD — a visitor who
              read this far has a concrete job in mind, which is the only
              moment the pitch is worth anything. */}
          <div
            style={{
              marginTop: 16,
              padding: '16px 18px',
              borderLeft: '3px solid var(--pri)',
              background: 'rgba(40,120,60,0.04)',
            }}
          >
            {signedIn ? (
              <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
                <AddToPipeline item={job} />
                <span style={{ fontSize: 13, color: 'var(--ink2)' }}>
                  评估匹配度后，可以在「简历定制」里生成针对这个岗位的简历。
                </span>
              </div>
            ) : (
              <>
                <div className="eyebrow">下一步</div>
                <div className="serif" style={{ fontSize: 20, margin: '4px 0 6px' }}>
                  注册后，AI 帮你申请这个岗位
                </div>
                <div style={{ fontSize: 13, color: 'var(--ink2)', marginBottom: 12, lineHeight: 1.6 }}>
                  自动评估你和岗位的匹配度，生成并润色针对性英文简历，还能帮你准备面试。
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                  <Link to={openSignup ? '/register' : '/login'} className="btn accent">
                    {openSignup ? '免费注册 →' : '登录 →'}
                  </Link>
                  {openSignup && (
                    <Link to="/login" className="btn ghost">已有账号，登录</Link>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div>
          <div className="card" style={{ padding: 18, marginBottom: 12 }}>
            <a
              href={job.url_direct || job.url}
              target="_blank"
              rel="noreferrer"
              className="btn accent"
              style={{ display: 'block', textAlign: 'center' }}
            >
              查看原职位 ↗
            </a>
            <div className="page-sub" style={{ marginTop: 10, textAlign: 'center', lineHeight: 1.6 }}>
              {job.url_direct
                ? '雇主官网直链。'
                : `跳转到雇主网站查看和申请。${source ? `来源：${source}。` : ''}`}
            </div>
            {/* Aggregator listings can die within hours of posting (verified
                live: a same-day Indeed row already 404ing on Indeed's side) —
                this one quiet line keeps a dead link from being a dead end. */}
            <div style={{ marginTop: 8, textAlign: 'center' }}>
              <a
                href={`https://www.google.com/search?q=${encodeURIComponent(`"${job.title}" ${job.company || ''} UK`)}`}
                target="_blank"
                rel="noreferrer"
                className="page-sub"
                style={{ textDecoration: 'underline' }}
              >
                打不开？搜索这个岗位 ↗
              </a>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
